import apiClient from '@/utils/axios.js';
import offlineDB from '@/indexedDB/offlineDB.js';
import { canMutateOnline } from '@/services/onlineCheckService.js';
import { isOfflineFlagEnabled } from '@/configs/offlineFeatureFlags.js';
import { checksumRows } from '@/services/syncChecksum.js';
import { recordSyncEvent } from '@/services/syncTelemetry.js';

/**
 * Membership reconciliation for the offline cache.
 *
 * Deltas and the change feed both answer "what changed". Neither can answer
 * "what should I still be holding", and that is where an offline cache rots: a
 * sale reassigned to another branch, a permission revoked, a row that aged past
 * the retention window. No delete is written for any of them, so the row simply
 * stays — correct-looking, indefinitely stale, and invisible to every other
 * check.
 *
 * Two passes, cheap first:
 *
 * 1. `verifyOfflineCaches` compares a local checksum against the server's. One
 *    small request for every subscription; no ids move unless something is off.
 * 2. `reconcileSubscriptions` fetches the id manifest only for the subscriptions
 *    that disagreed, and evicts what the server no longer lists.
 */

/**
 * Rows the manifest is authoritative for, mapped to their Dexie store.
 *
 * Exported because the union guard below is defined over *stores*, not
 * subscriptions: two subscriptions may legitimately share one, and the eviction
 * rule only makes sense when you can see which.
 */
export const SUBSCRIPTION_STORES = {
    branches: 'branches',
    suppliers: 'suppliers',
    customers: 'customers',
    automotives: 'automotives',
    accounts: 'accounts',
    delivery_persons: 'delivery_persons',
    workers: 'workers',
    cash_commitments: 'cash_commitments',
    sales: 'sales',
    purchases: 'purchases',
    transfers: 'transfers',
    gate_passes: 'gate_passes',
    load_forms: 'load_forms',
    expenses: 'expenses',
    wholesaler_order_requests: 'wholesaler_order_requests',
    shelf_locations: 'shelf_locations',
    variant_locations: 'variant_locations',
};

const DETAIL_TABLE_FOR = {
    branches: 'branch_details',
    suppliers: 'supplier_details',
    customers: 'customer_details',
    purchases: 'purchase_details',
    sales: 'sales_details',
    transfers: 'transfer_details',
    gate_passes: 'gate_pass_details',
    wholesaler_order_requests: 'order_request_details',
    expenses: 'expense_details',
    accounts: 'account_details',
    automotives: 'automotive_details',
    load_forms: 'load_form_details',
    delivery_persons: 'delivery_person_details',
    workers: 'worker_details',
};

function unwrap(response) {
    const body = response?.data;
    return body?.data && !Array.isArray(body.data) ? body.data : body;
}

/**
 * The slice of a local store the server's summary is actually about.
 *
 * A truncated summary covers the newest `limit` ids and stops. Checksumming the
 * whole cache against it can then never agree — a cache larger than the cap
 * reports drift on every single check, which is worse than no check at all: it
 * costs a manifest fetch each convergence and trains everyone to ignore the one
 * signal that says the cache is wrong. Comparing the same window makes a
 * mismatch mean something again.
 */
function comparableRows(rows, summary) {
    if (!summary?.truncated || !Number.isFinite(summary.limit) || rows.length <= summary.limit) {
        return rows;
    }

    // Newest ids first, matching the manifest's own `orderByDesc(id)`.
    return [...rows].sort((a, b) => Number(b.id) - Number(a.id)).slice(0, summary.limit);
}

/**
 * Lowest id this manifest is allowed to speak about.
 *
 * An untruncated manifest lists everything the reader should hold, so absence
 * anywhere means "evict" and the floor is open. A truncated one stopped at its
 * cap: it enumerated the newest N ids and knows nothing older, so the floor is
 * its own lowest id and anything beneath is left alone. Without this a cache
 * holding more rows than the cap loses its tail on every reconcile and refetches
 * it on every sync — an eviction loop that looks exactly like random data loss.
 */
function evictionFloor(manifest) {
    const ids = manifest?.ids ?? [];
    if (!manifest?.truncated || ids.length === 0) return -Infinity;

    let lowest = Infinity;
    for (const id of ids) {
        const numeric = Number(id);
        if (Number.isFinite(numeric) && numeric < lowest) lowest = numeric;
    }

    return Number.isFinite(lowest) ? lowest : -Infinity;
}

async function localRows(entity) {
    const store = offlineDB[SUBSCRIPTION_STORES[entity]];
    if (!store) return null;
    return store.toArray();
}

/**
 * Compare local checksums with the server's.
 *
 * @returns {Promise<{ok: boolean, reason?: string, drifted: string[], checked: number}>}
 */
export async function verifyOfflineCaches({ reason = 'manual', subscriptions = null } = {}) {
    if (!isOfflineFlagEnabled('subscriptionReconcile')) {
        return { ok: false, reason: 'flag_disabled', drifted: [], checked: 0 };
    }
    if (!canMutateOnline()) {
        return { ok: false, reason: 'offline', drifted: [], checked: 0 };
    }

    try {
        const response = await apiClient.get('sync/checksums', {
            params: subscriptions ? { subscriptions } : {},
            offlinePolicy: 'read',
            skipProgress: true,
        });

        const body = unwrap(response) || {};
        const drifted = [];
        let checked = 0;

        for (const [entity, expected] of Object.entries(body.subscriptions ?? {})) {
            const rows = await localRows(entity);
            // A store this session never seeded is not drift — there is nothing
            // to be wrong yet, and the seed will bring it.
            if (rows === null || rows.length === 0) continue;

            checked += 1;
            const actual = checksumRows(comparableRows(rows, expected));

            if (actual.count !== expected.count || actual.checksum !== expected.checksum) {
                drifted.push(entity);
            }
        }

        if (drifted.length) {
            recordSyncEvent('drift_detected', { reason, entities: drifted });
        }

        return { ok: true, drifted, checked };
    } catch (error) {
        console.warn('[subscriptions] checksum verification failed', reason, error);
        return { ok: false, reason: 'error', error, drifted: [], checked: 0 };
    }
}

/**
 * Evict rows the server no longer lists for a subscription.
 *
 * The union guard matters: the same sale is reachable through its origin branch,
 * its payment branch and the company-wide view, so a row missing from one
 * manifest may still be legitimately held through another. PowerSync states the
 * rule directly — a row is only removed once it has left *every* subscription
 * the client holds. Evicting on a single manifest's absence would delete rows
 * the user can still open.
 */
export async function reconcileSubscriptions({ reason = 'manual', subscriptions = null } = {}) {
    if (!isOfflineFlagEnabled('subscriptionReconcile')) {
        return { ok: false, reason: 'flag_disabled', evicted: 0 };
    }
    if (!canMutateOnline()) {
        return { ok: false, reason: 'offline', evicted: 0 };
    }

    try {
        const response = await apiClient.get('sync/manifest', {
            params: subscriptions ? { subscriptions } : {},
            offlinePolicy: 'read',
            skipProgress: true,
        });

        const body = unwrap(response) || {};
        const manifests = body.subscriptions ?? {};

        // Union every id the server listed anywhere in this response, keyed by
        // store, before deciding what may go.
        const keepByStore = new Map();
        for (const [entity, manifest] of Object.entries(manifests)) {
            const store = SUBSCRIPTION_STORES[entity];
            if (!store) continue;
            if (!keepByStore.has(store)) keepByStore.set(store, new Set());
            const keep = keepByStore.get(store);
            for (const id of manifest.ids ?? []) keep.add(String(id));
        }

        // How far down a store's manifests actually reach. A manifest is ordered
        // newest-id first and stops at its cap, so it enumerates membership only
        // at or above its lowest id — below that it has no opinion, and reading
        // "absent" as "revoked" there deletes the whole tail of a cache larger
        // than the cap. Taking the lowest floor across the store's subscriptions
        // keeps the union rule intact: a row is safe if *any* manifest covers it.
        const floorByStore = new Map();
        for (const [entity, manifest] of Object.entries(manifests)) {
            const store = SUBSCRIPTION_STORES[entity];
            if (!store) continue;
            const floor = evictionFloor(manifest);
            const current = floorByStore.get(store);
            floorByStore.set(store, current === undefined ? floor : Math.min(current, floor));
        }

        let evicted = 0;
        const missing = {};

        for (const [entity, manifest] of Object.entries(manifests)) {
            const storeName = SUBSCRIPTION_STORES[entity];
            const store = offlineDB[storeName];
            if (!store) continue;

            const rows = await store.toArray();
            // Never prune a store this session has not seeded: an empty cache
            // and a fully-evicted one look identical here.
            if (rows.length === 0) continue;

            const keep = keepByStore.get(storeName) ?? new Set();
            const floor = floorByStore.get(storeName) ?? -Infinity;
            const stale = rows.filter((row) => !keep.has(String(row.id)) && Number(row.id) >= floor).map((row) => row.id);

            if (stale.length) {
                await store.bulkDelete(stale);
                const detail = offlineDB[DETAIL_TABLE_FOR[entity]];
                if (detail) await detail.bulkDelete(stale);
                evicted += stale.length;
            }

            // Rows the server lists that we do not hold. The delta sync fills
            // these; reporting them lets the caller decide whether to escalate.
            const held = new Set(rows.map((row) => String(row.id)));
            const absent = (manifest.ids ?? []).filter((id) => !held.has(String(id)));
            if (absent.length) missing[entity] = absent.length;
        }

        if (evicted || Object.keys(missing).length) {
            recordSyncEvent('reconciled', { reason, evicted, missing });
        }

        return { ok: true, evicted, missing, revision: body.revision };
    } catch (error) {
        console.warn('[subscriptions] reconcile failed', reason, error);
        return { ok: false, reason: 'error', error, evicted: 0 };
    }
}

export default { verifyOfflineCaches, reconcileSubscriptions, SUBSCRIPTION_STORES };
